import { useDndContext } from '@dnd-kit/core';
import { useEffect } from 'react';
import { findNearestScroller } from '@utils/common';
import { easeIn } from '@utils/constants';

const threshold = {
  x: 0.1,
  y: 0.1
};
const maxSpeedPerFrameInPx = {
  x: 8,
  y: 5
};

/**
 * Frustratingly dnd-kit does not support autoscroll while having a horsiontal
 * scroller nested within its DnDContext.
 *
 * As such we provide a custom integration of autoscrolling for tasks that have
 * a drawer.
 */
function TaskAutoScroller(args) {
  const {
    dragOverlay,
    measuringConfiguration
  } = useDndContext();
  useEffect(() => {
    const dragOverlayNode = dragOverlay.nodeRef.current;
    if (!dragOverlayNode) {
      return;
    }
    let frameId;
    const intialPosition = measuringConfiguration.dragOverlay.measure(dragOverlayNode);
    const verticalContainer = findNearestScroller(args.taskRef, "vertical") ?? document.documentElement;
    const onUpdate = () => {
      const rect = measuringConfiguration.dragOverlay.measure(dragOverlayNode);
      const relativeVerticalRect = {
        top: rect.top / window.innerHeight,
        bottom: rect.bottom / window.innerHeight
      };
      const canScrollUp = relativeVerticalRect.top === 0 || rect.top < intialPosition.top;
      const canScrollDown = relativeVerticalRect.bottom === 0 || rect.bottom > intialPosition.bottom;
      if (canScrollUp && relativeVerticalRect.top < threshold.y) {
        // Scroll up
        const p = (threshold.y - relativeVerticalRect.top) / threshold.y;
        const speed = maxSpeedPerFrameInPx.y * easeIn(p, 2);
        verticalContainer.scrollTo({
          top: verticalContainer.scrollTop - speed,
          behavior: "instant"
        });
      } else if (canScrollDown && relativeVerticalRect.bottom > 1 - threshold.y) {
        // Scroll down
        const p = (threshold.y - (1 - relativeVerticalRect.bottom)) / threshold.y;
        const speed = maxSpeedPerFrameInPx.y * easeIn(p, 2);
        verticalContainer.scrollTo({
          top: verticalContainer.scrollTop + speed,
          behavior: "instant"
        });
      }
      const horisontalScroller = args.horisontalScroller?.current;
      if (horisontalScroller) {
        const horisontalRect = horisontalScroller.getBoundingClientRect();
        const dragOverlayIntersects = rect.top < horisontalRect.bottom && rect.bottom > horisontalRect.top;
        if (dragOverlayIntersects) {
          const relativeHorisontalRect = {
            left: Math.max(0, (rect.left - horisontalRect.left) / horisontalRect.width),
            right: Math.min((rect.right - horisontalRect.left) / horisontalRect.width, 1)
          };
          const canScrollLeft = relativeHorisontalRect.left === 0 || rect.left < intialPosition.left;
          const canScrollRight = relativeHorisontalRect.right === 1 || rect.right > intialPosition.right;
          if (canScrollLeft && relativeHorisontalRect.left < threshold.x) {
            // Scroll left
            const p = (threshold.x - relativeHorisontalRect.left) / threshold.x;
            const speed = maxSpeedPerFrameInPx.x * easeIn(p, 2);
            horisontalScroller.scrollTo({
              left: horisontalScroller.scrollLeft - speed,
              behavior: "instant"
            });
          } else if (canScrollRight && relativeHorisontalRect.right > 1 - threshold.x) {
            // Scroll right
            const p = (threshold.x - (1 - relativeHorisontalRect.right)) / threshold.x;
            const speed = maxSpeedPerFrameInPx.x * easeIn(p, 2);
            horisontalScroller.scrollTo({
              left: horisontalScroller.scrollLeft + speed,
              behavior: "instant"
            });
          }
        }
      }
      frameId = requestAnimationFrame(onUpdate);
    };
    frameId = requestAnimationFrame(onUpdate);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [args.horisontalScroller, args.taskRef, dragOverlay.nodeRef, dragOverlay.rect, measuringConfiguration.dragOverlay]);
  return null;
}

export { TaskAutoScroller };
//# sourceMappingURL=dnd-kit.auto-scroll.js.map
